import React, { useEffect, useState } from "react";
import { useStore } from "../store";
import api from "../api";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import { SearchableCountrySelect } from "../site/formFields";
import { findCountry, flagOf } from "../site/countries";
import {
  TrendingUp, Send, ArrowDownToLine, ArrowLeftRight, Plus, Copy, Check,
  Wallet as WalletIcon, ShoppingBag, Landmark, Shield, QrCode, Clock,
  Sparkles, Users, Gift, BarChart3, AlertCircle, Package, Download,
  Camera, Edit3, Save, X, GraduationCap
} from "lucide-react";
import { useT } from "../site/i18n";

/* ═══════════════════ Reusable helpers ═══════════════════ */
const Page = ({ title, sub, children, testId }) => (
  <div data-testid={testId}>
    <h2 className="page-title">{title}</h2>
    {sub && <p className="page-sub">{sub}</p>}
    {children}
  </div>
);
const Card = ({ children, style = {}, testId, ...p }) => (
  <div className="tg-card" data-testid={testId} style={{ padding: 22, ...style }} {...p}>{children}</div>
);
const fmt = (n, d = 2) => Number(n || 0).toFixed(d);

/* ═══════════════════ 1. OVERVIEW ═══════════════════ */
export function OverviewPage() {
  const { t } = useT();
  const { user, wallets: storeWallets, transactions, fetchAll } = useStore();
  // Refresh wallets + transactions every 15s so the overview stays live.
  useAutoRefresh(() => fetchAll(), 15000);
  const w = storeWallets || user?.wallets || {};
  const totalTGC = (w.cashTgc || 0) + (w.shoppingTgc || 0) + (w.pensionTgc || 0) + (w.insuranceTgc || 0);
  const perSecond = ((w.pensionTgc || 0) + (w.insuranceTgc || 0)) * (0.08 / 30) / 86400;
  const [earned, setEarned] = useState(0);
  useEffect(() => {
    if (perSecond <= 0) return;
    const start = Date.now();
    const t = setInterval(() => setEarned(((Date.now() - start) / 1000) * perSecond), 100);
    return () => clearInterval(t);
  }, [perSecond]);

  return (
    <Page title={t("user.e90d86")} sub={`${user?.firstName || t("user.participant")}, ${t("user.portfolio-growing")}${user?.createdAt ? " · 📅 " + new Date(user.createdAt).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}`} testId="overview-page">
      {/* Live ticker */}
      <Card style={{ background: "linear-gradient(135deg, #0D1B4B 0%, #1A3A8F 50%, #0E6B5E 100%)", marginBottom: 20, color: "white", position: "relative" }}>
        <div style={{ position: "absolute", top: 16, right: 16, display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 8, height: 8, borderRadius: 4, background: "#10F4A7", animation: "pulse 1.2s infinite" }} />
          <span style={{ fontSize: 11 }}>LIVE STAKING</span>
        </div>
        <div style={{ fontSize: 12, opacity: 0.8 }}>{t("user.8cf2b1")}</div>
        <div style={{ fontSize: 42, fontWeight: 900, letterSpacing: -1, marginTop: 6, fontFeatureSettings: '"tnum"' }}>
          {fmt((w.pensionTgc || 0) + (w.insuranceTgc || 0) + earned, 6)}
          <span style={{ fontSize: 16, opacity: 0.7, marginLeft: 8 }}>TGC</span>
        </div>
      </Card>

      {/* 4 wallets */}
      <div className="dash-grid dash-grid-4" data-testid="wallets-grid">
        {[
          { k: "cash",      ic: "💰", l: "Cash",      v: w.cashTgc || 0,      sub: t("user.56c3fc") },
          { k: "shopping",  ic: "🛒", l: "Shopping",  v: w.shoppingTgc || 0,  sub: t("user.19357d") },
          { k: "pension",   ic: "🏛️", l: "Pension",   v: w.pensionTgc || 0,   sub: t("user.48369d") },
          { k: "insurance", ic: "🛡️", l: "Insurance", v: w.insuranceTgc || 0, sub: t("user.420b4a") },
        ].map((c) => (
          <div key={c.k} className={`wallet-card ${c.k}`} data-testid={`wallet-${c.k}`}>
            <div style={{ fontSize: 26 }}>{c.ic}</div>
            <div style={{ fontSize: 12, opacity: 0.8, marginTop: 8 }}>{c.l} Wallet</div>
            <div style={{ fontSize: 26, fontWeight: 900, marginTop: 4, fontFeatureSettings: '"tnum"' }}>{fmt(c.v)}</div>
            <div style={{ fontSize: 10, opacity: 0.65 }}>TGC · €{fmt(c.v * 0.10)}</div>
            <div style={{ fontSize: 11, opacity: 0.7, marginTop: 8 }}>{c.sub}</div>
          </div>
        ))}
      </div>

      {/* Quick actions */}
      <h3 style={{ margin: "28px 0 14px", fontSize: 16 }}>{t("user.b05019")}</h3>
      <div className="dash-grid dash-grid-4">
        {[
          [ t("user.6da0f0"),  <ArrowLeftRight size={18} />,  "/dashboard/wallets"],
          [ t("user.e5ffc9"),<QrCode size={18} />,           "/dashboard/wallets"],
          [ t("header.products.staking.title"),   <TrendingUp size={18} />,       "/dashboard/staking"],
          ["SWIFT",      <Send size={18} />,              "/dashboard/swift"],
        ].map(([l, ic, to], i) => (
          <a key={i} href={to} className="tg-card" style={{ padding: 16, textAlign: "center", textDecoration: "none", color: "inherit" }}>
            <div style={{ margin: "0 auto 8px", color: "var(--tg-blue)" }}>{ic}</div>
            <div style={{ fontSize: 13, fontWeight: 700 }}>{l}</div>
          </a>
        ))}
      </div>

      {/* Recent transactions */}
      <h3 style={{ margin: "28px 0 14px", fontSize: 16 }}>{t("user.fa638c")}</h3>
      <Card style={{ padding: 0 }}>
        <table className="dash-table">
          <thead><tr><th>{t("pages3.3e729b")}</th><th>{t("pages3.345805")}</th><th>{t("user.e9b249")}</th><th style={{ textAlign: "right" }}>{t("pages3.cf59eb")}</th></tr></thead>
          <tbody>
            {(transactions || []).slice(0, 5).map((tx, i) => (
              <tr key={i}>
                <td style={{ color: "var(--tg-text-hint)" }}>{tx.timestamp?.slice(5, 16) || "—"}</td>
                <td>{tx.type || "—"}</td>
                <td>{tx.wallet || "—"}</td>
                <td style={{ textAlign: "right", color: (tx.amount || 0) >= 0 ? "#10F4A7" : "#FF3D6C", fontWeight: 700 }}>
                  {(tx.amount || 0) >= 0 ? "+" : ""}{fmt(tx.amount)}
                </td>
              </tr>
            ))}
            {(!transactions || transactions.length === 0) && <tr><td colSpan={4} style={{ textAlign: "center", padding: 40, color: "var(--tg-text-hint)" }}>{t("user.241618")}</td></tr>}
          </tbody>
        </table>
      </Card>
    </Page>
  );
}

/* ═══════════════════ 2. WALLETS ═══════════════════ */
export function WalletsPage() {
  const { t } = useT();
  const { user, setToast, fetchAll } = useStore();
  useAutoRefresh(() => fetchAll(), 15000);
  const w = user?.wallets || {};
  const topup = async () => { try { await api.post("/wallets/test-topup"); setToast("success", "+1000 TGC"); fetchAll(); } catch {} };

  return (
    <Page title={t("user.762d97")} sub={t("user.466c71")} testId="wallets-page">
      <div className="dash-grid dash-grid-2">
        {[
          { k: "cash", t: "Cash Wallet", v: w.cashTgc || 0, desc: t("user.69ec1f"), actions: [[t("user.79cabd"), "withdraw"], [t("user.b93e8b"), "transfer"], [t("user.719918"), topup]] },
          { k: "shopping", t: "Shopping Wallet", v: w.shoppingTgc || 0, desc: t("user.8220f5"), actions: [[t("user.18e780"), "scan"]] },
          { k: "pension", t: "Pension Wallet", v: w.pensionTgc || 0, desc: t("user.243d24") + fmt((w.pensionTgc || 0) * 0.10 * Math.pow(1.08, 60)), actions: [[t("user.68745f"), "lock"]] },
          { k: "insurance", t: "Insurance Wallet", v: w.insuranceTgc || 0, desc: t("user.9215fe"), actions: [[t("user.68745f"), "lock"]] },
        ].map((c) => (
          <div key={c.k} className={`wallet-card ${c.k}`} data-testid={`wallet-page-${c.k}`}>
            <div style={{ fontSize: 14, opacity: 0.9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>{c.t}</div>
            <div style={{ fontSize: 42, fontWeight: 900, marginTop: 8, fontFeatureSettings: '"tnum"' }}>{fmt(c.v)}</div>
            <div style={{ fontSize: 13, opacity: 0.7 }}>TGC · €{fmt(c.v * 0.10)}</div>
            <p style={{ fontSize: 12, opacity: 0.85, marginTop: 16, lineHeight: 1.5 }}>{c.desc}</p>
            <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
              {c.actions.map(([label, action], i) => (
                <button key={i} onClick={typeof action === "function" ? action : () => setToast("info", `${label} — ${t("user.mobile-only")}`)}
                  style={{ padding: "8px 14px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.3)", background: "rgba(255,255,255,0.1)", color: "white", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Page>
  );
}

/* ═══════════════════ 3. STAKING ═══════════════════ */
export function StakingPage() {
  const { t } = useT();
  const { user, wallets: storeWallets, fetchAll } = useStore();
  const w = storeWallets || user?.wallets || {};
  const [stakes, setStakes] = useState([]);
  const [stats, setStats] = useState({ activeCount: 0, totalInvestedTgc: 0, totalAccruedTgc: 0, dailyIncomeTgc: 0, totalInvestedEur: 0, totalAccruedEur: 0, dailyIncomeEur: 0 });
  const [showForm, setShowForm] = useState(false);
  const [pkgId, setPkgId] = useState(1);
  const [amountEur, setAmountEur] = useState("100");
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const packages = [
    { id: 1, n: "Starter",  rate: 8,  days: 30,  col: "#00F2FF" },
    { id: 2, n: "Bronze",   rate: 10, days: 60,  col: "#10F4A7" },
    { id: 3, n: "Silver",   rate: 13, days: 90,  col: "#FFD700" },
    { id: 4, n: "Gold",     rate: 17, days: 150, col: "#F97316" },
    { id: 5, n: "Diamond",  rate: 20, days: 300, col: "#BC13FE" },
  ];
  const selectedPkg = packages.find((p) => p.id === pkgId) || packages[0];
  const amountNum = Math.max(0, parseFloat(amountEur) || 0);
  const tgcAmount = amountNum / 0.10;
  const projectedDailyTgc = tgcAmount * (selectedPkg.rate / 100) / 30;
  const projectedTotalTgc = projectedDailyTgc * selectedPkg.days;
  const projectedTotalEur = projectedTotalTgc * 0.10;

  const load = async () => {
    try {
      const r = await api.get("/staking/my");
      setStakes(r.data.stakes || []);
      setStats(r.data.stats || {});
    } catch {}
  };
  useEffect(() => { load(); fetchAll().catch(() => {}); }, []);
  // Auto-refresh stakes list every 15s
  useAutoRefresh(load, 15000);

  const showToast = (text, kind = "ok") => { setToast({ text, kind }); setTimeout(() => setToast(null), 3000); };

  const submit = async () => {
    if (amountNum < 100 || amountNum > 10000) {
      showToast("Сумма должна быть от 100€ до 10 000€", "err"); return;
    }
    if ((w.cashTgc || 0) < tgcAmount) {
      showToast(`Недостаточно TGC. Нужно ${tgcAmount.toFixed(2)} TGC в Cash-кошельке`, "err"); return;
    }
    setBusy(true);
    try {
      await api.post("/staking/create", { packageId: pkgId, amountEur: amountNum });
      showToast(`✅ Стейк создан: €${amountNum.toFixed(0)} @ ${selectedPkg.rate}%/мес`);
      setShowForm(false);
      setAmountEur("100");
      await load();
      await fetchAll();
    } catch (e) {
      showToast(e?.response?.data?.detail || "Ошибка создания стейка", "err");
    } finally { setBusy(false); }
  };

  const withdraw = async (stakeId) => {
    if (!window.confirm("Закрыть стейк и вернуть депозит в Cash-кошелёк?")) return;
    try {
      await api.post(`/staking/${stakeId}/withdraw`);
      showToast("✅ Депозит возвращён в Cash-кошелёк");
      await load();
      await fetchAll();
    } catch (e) {
      showToast(e?.response?.data?.detail || "Ошибка", "err");
    }
  };

  return (
    <Page title={t("user.8f2b02")} sub={t("user.949fe3")} testId="staking-page">
      {/* Stats overview */}
      <div className="dash-grid dash-grid-4" style={{ marginBottom: 22 }} data-testid="staking-stats">
        <Card style={{ background: "linear-gradient(135deg, #0D1B4B, #1A3A8F)", color: "white", borderColor: "rgba(0,242,255,0.3)" }}>
          <div style={{ fontSize: 11, opacity: 0.8, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>В стейкинге</div>
          <div style={{ fontSize: 26, fontWeight: 900, marginTop: 6 }}>{fmt(stats.totalInvestedTgc)} <span style={{ fontSize: 12, opacity: 0.7 }}>TGC</span></div>
          <div style={{ fontSize: 12, opacity: 0.8 }}>€{fmt(stats.totalInvestedEur)} · {stats.activeCount || 0} активных</div>
        </Card>
        <Card style={{ background: "linear-gradient(135deg, #0E6B5E, #10F4A7)", color: "#001b13", borderColor: "rgba(16,244,167,0.4)" }}>
          <div style={{ fontSize: 11, opacity: 0.7, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>Всего начислено</div>
          <div style={{ fontSize: 26, fontWeight: 900, marginTop: 6 }}>+{fmt(stats.totalAccruedTgc, 4)} <span style={{ fontSize: 12, opacity: 0.7 }}>TGC</span></div>
          <div style={{ fontSize: 12, opacity: 0.8 }}>€{fmt(stats.totalAccruedEur)}</div>
        </Card>
        <Card style={{ background: "linear-gradient(135deg, #FFD700, #F97316)", color: "#2A1500", borderColor: "rgba(255,215,0,0.5)" }}>
          <div style={{ fontSize: 11, opacity: 0.8, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>Доход в сутки</div>
          <div style={{ fontSize: 26, fontWeight: 900, marginTop: 6 }}>+{fmt(stats.dailyIncomeTgc, 4)} <span style={{ fontSize: 12, opacity: 0.7 }}>TGC</span></div>
          <div style={{ fontSize: 12, opacity: 0.8 }}>€{fmt(stats.dailyIncomeEur)} / день</div>
        </Card>
        <Card style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: "center", borderStyle: "dashed", borderColor: "var(--tg-blue)" }}>
          <div style={{ fontSize: 28, marginBottom: 4 }}>💎</div>
          <button className="tg-btn gold" onClick={() => setShowForm(true)} data-testid="open-stake-form" style={{ justifyContent: "center", fontWeight: 800 }}>
            <Plus size={14} /> Новый стейк
          </button>
          <div style={{ fontSize: 11, color: "var(--tg-text-hint)", marginTop: 8 }}>от €100 до €10 000</div>
        </Card>
      </div>

      {/* Active stakes */}
      <h3 style={{ margin: "0 0 14px", fontSize: 16 }}>{t("user.2b783a")}</h3>
      {stakes.length === 0 && <Card data-testid="staking-empty"><p style={{ color: "var(--tg-text-hint)", margin: 0 }}>{t("user.487850")}</p></Card>}
      {stakes.map((s) => (
        <Card key={s.stakeId} style={{ marginBottom: 12 }} testId={`stake-${s.stakeId}`}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 14 }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
                <span>{s.packageName || "Staking"}</span>
                <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: s.status === "active" ? "rgba(16,244,167,0.15)" : s.status === "completed" ? "rgba(255,215,0,0.15)" : "rgba(255,61,108,0.15)", color: s.status === "active" ? "#10F4A7" : s.status === "completed" ? "#FFD700" : "#FF3D6C", fontWeight: 700, textTransform: "uppercase" }}>
                  {s.status === "active" ? "Активен" : s.status === "completed" ? "Завершён" : "Закрыт"}
                </span>
              </div>
              <div style={{ fontSize: 12, color: "var(--tg-text-dim)", marginTop: 2 }}>
                {fmt(s.amountTgc)} TGC (€{fmt(s.amountEur || 0)}) · {s.monthlyRate}%/мес · {s.duration} дней · {s.daysElapsed || 0}/{s.duration} дн ({s.progressPct?.toFixed(1) || 0}%)
              </div>
              <div style={{ height: 6, background: "var(--tg-border)", borderRadius: 3, marginTop: 8, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${Math.min(100, s.progressPct || 0)}%`, background: "linear-gradient(90deg, #00F2FF, #10F4A7)", borderRadius: 3 }} />
              </div>
            </div>
            <div style={{ textAlign: "right", minWidth: 180 }}>
              <div style={{ fontSize: 11, color: "var(--tg-text-hint)" }}>Доход в сутки</div>
              <div style={{ color: "#10F4A7", fontWeight: 800, fontSize: 16 }}>+{fmt(s.dailyAccrualTgc, 4)} TGC</div>
              <div style={{ fontSize: 11, color: "var(--tg-text-hint)", marginTop: 4 }}>Начислено всего</div>
              <div style={{ color: "#FFD700", fontWeight: 800, fontSize: 14 }}>+{fmt(s.accruedTgc || s.profitTgc || 0, 4)} TGC</div>
            </div>
            {s.status !== "withdrawn" && (
              <button className="tg-btn ghost" onClick={() => withdraw(s.stakeId)} data-testid={`withdraw-stake-${s.stakeId}`} style={{ borderColor: "#FF3D6C", color: "#FF3D6C" }}>
                Закрыть и вернуть
              </button>
            )}
          </div>
        </Card>
      ))}

      {/* Packages grid */}
      <h3 style={{ margin: "28px 0 14px", fontSize: 16 }}>{t("user.a4eabd")}</h3>
      <div className="dash-grid dash-grid-3">
        {packages.map((p) => {
          const profit100 = Math.round(100 * p.rate / 100 * (p.days / 30));
          return (
            <Card key={p.n} style={{ borderColor: p.col + "55", position: "relative", overflow: "hidden", cursor: "pointer" }} onClick={() => { setPkgId(p.id); setShowForm(true); }}>
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: p.col }} />
              <div style={{ color: p.col, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>{p.n}</div>
              <div style={{ fontSize: 38, fontWeight: 900, marginTop: 8, color: p.col }}>{p.rate}%</div>
              <div style={{ fontSize: 11, color: "var(--tg-text-hint)" }}>в месяц</div>
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px dashed var(--tg-border)", fontSize: 13 }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ color: "var(--tg-text-dim)" }}>Срок:</span><b>{p.days} дн</b></div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}><span style={{ color: "var(--tg-text-dim)" }}>Прибыль с €100:</span><b style={{ color: "#10F4A7" }}>+€{profit100}</b></div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}><span style={{ color: "var(--tg-text-dim)" }}>Сумма:</span><b>€100 — €10 000</b></div>
              </div>
              <button className="tg-btn ghost" style={{ width: "100%", justifyContent: "center", marginTop: 14, borderColor: p.col + "88", color: p.col }} data-testid={`stake-plan-${p.id}`}>
                Выбрать
              </button>
            </Card>
          );
        })}
      </div>

      {/* Form modal */}
      {showForm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }} onClick={() => setShowForm(false)} data-testid="stake-form-modal">
          <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--tg-surface)", border: "1px solid var(--tg-border)", borderRadius: 16, padding: 28, maxWidth: 560, width: "100%", maxHeight: "92vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
              <h3 style={{ margin: 0, fontSize: 18 }}>💎 Новый стейк</h3>
              <button className="dash-icon-btn" onClick={() => setShowForm(false)} data-testid="close-stake-form"><X size={16} /></button>
            </div>

            <div style={{ fontSize: 13, color: "var(--tg-text-hint)", marginBottom: 10 }}>Выберите план:</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(95px,1fr))", gap: 8, marginBottom: 18 }}>
              {packages.map((p) => (
                <button key={p.id} onClick={() => setPkgId(p.id)} data-testid={`select-plan-${p.id}`}
                  style={{
                    padding: 10, borderRadius: 10, cursor: "pointer",
                    background: pkgId === p.id ? p.col + "22" : "var(--tg-bg)",
                    border: `2px solid ${pkgId === p.id ? p.col : "var(--tg-border)"}`,
                    color: pkgId === p.id ? p.col : "var(--tg-text)",
                    fontSize: 12, fontWeight: 700, textAlign: "center"
                  }}>
                  <div style={{ fontSize: 18, fontWeight: 900 }}>{p.rate}%</div>
                  <div style={{ fontSize: 10, opacity: 0.8, marginTop: 2 }}>{p.days} дн</div>
                </button>
              ))}
            </div>

            <div style={{ fontSize: 13, color: "var(--tg-text-hint)", marginBottom: 6 }}>Сумма (EUR), от 100 до 10 000:</div>
            <input
              type="number" min={100} max={10000} step={50}
              value={amountEur}
              onChange={(e) => setAmountEur(e.target.value)}
              className="auth-input"
              style={{ paddingLeft: 14, marginBottom: 6 }}
              data-testid="stake-amount-eur"
            />
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
              {[100, 500, 1000, 5000, 10000].map((v) => (
                <button key={v} onClick={() => setAmountEur(String(v))} data-testid={`stake-quick-${v}`}
                  style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid var(--tg-border)", background: "transparent", color: "var(--tg-text-dim)", fontSize: 11, cursor: "pointer" }}>
                  €{v.toLocaleString("ru-RU")}
                </button>
              ))}
            </div>

            <div style={{ background: "var(--tg-bg)", border: "1px solid var(--tg-border)", borderRadius: 10, padding: 14, marginBottom: 18 }}>
              <div style={{ fontSize: 11, color: "var(--tg-text-hint)", marginBottom: 8, textTransform: "uppercase", fontWeight: 700, letterSpacing: 1 }}>Расчёт</div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 13 }}><span style={{ color: "var(--tg-text-dim)" }}>В TGC:</span><b>{fmt(tgcAmount)} TGC</b></div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 13 }}><span style={{ color: "var(--tg-text-dim)" }}>Доход в сутки:</span><b style={{ color: "#10F4A7" }}>+{fmt(projectedDailyTgc, 4)} TGC (€{fmt(projectedDailyTgc * 0.10, 2)})</b></div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 13 }}><span style={{ color: "var(--tg-text-dim)" }}>Всего за {selectedPkg.days} дн:</span><b style={{ color: "#FFD700" }}>+{fmt(projectedTotalTgc, 2)} TGC (€{fmt(projectedTotalEur, 2)})</b></div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 12, borderTop: "1px dashed var(--tg-border)", marginTop: 6, paddingTop: 8 }}><span style={{ color: "var(--tg-text-dim)" }}>Cash баланс:</span><b style={{ color: (w.cashTgc || 0) >= tgcAmount ? "#10F4A7" : "#FF3D6C" }}>{fmt(w.cashTgc || 0)} TGC</b></div>
            </div>

            <button className="tg-btn gold" onClick={submit} disabled={busy} data-testid="submit-stake"
              style={{ width: "100%", justifyContent: "center", fontWeight: 800, opacity: busy ? 0.6 : 1 }}>
              {busy ? "Создание..." : `Запустить стейк на €${amountNum.toFixed(0)}`}
            </button>
            <div style={{ fontSize: 11, color: "var(--tg-text-hint)", marginTop: 10, textAlign: "center" }}>
              Начисления автоматически 1 раз в сутки в Cash-кошелёк
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div data-testid="stake-toast" style={{ position: "fixed", bottom: 24, right: 24, padding: "12px 18px", borderRadius: 10, background: toast.kind === "err" ? "#FF3D6C" : "#10F4A7", color: toast.kind === "err" ? "#fff" : "#001b13", fontWeight: 700, fontSize: 13, boxShadow: "0 10px 30px rgba(0,0,0,0.4)", zIndex: 2000 }}>
          {toast.text}
        </div>
      )}
    </Page>
  );
}

/* ═══════════════════ 4. CARD ═══════════════════ */
export function CardPage() {
  const { t } = useT();
  const [frozen, setFrozen] = useState(false);
  return (
    <Page title="💳 Visa Card" sub={t("user.0f0cf1")} testId="card-page">
      <div className="dash-grid dash-grid-2">
        <div style={{ aspectRatio: "1.58/1", borderRadius: 20, padding: 28,
          background: "linear-gradient(135deg, #0D1B4B 0%, #1A3A8F 40%, #0E6B5E 100%)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.5), 0 0 80px rgba(0,242,255,0.15)",
          position: "relative", color: "white", filter: frozen ? "grayscale(0.8) blur(1px)" : "none", transition: "filter .3s" }} data-testid="visa-card">
          <div style={{ position: "absolute", top: 20, right: 20, fontWeight: 900, fontSize: 28, color: "#FFD700" }}>VISA</div>
          <div style={{ fontSize: 14, opacity: 0.8, fontWeight: 700, letterSpacing: 2 }}>TURBO GLOBAL</div>
          <div style={{ position: "absolute", bottom: 100, left: 28 }}>
            <div style={{ width: 44, height: 32, borderRadius: 6, background: "linear-gradient(135deg, #FFD700, #C89A00)" }} />
          </div>
          <div style={{ position: "absolute", bottom: 56, left: 28, fontSize: 22, letterSpacing: 3, fontFamily: "monospace" }}>5486 •••• •••• 4444</div>
          <div style={{ position: "absolute", bottom: 22, left: 28, right: 28, display: "flex", justifyContent: "space-between", fontSize: 12 }}>
            <span>CARDHOLDER</span><span>12/29</span>
          </div>
        </div>
        <Card>
          <div style={{ fontSize: 11, color: "var(--tg-blue)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>{t("user.2c34bf")}</div>
          <div style={{ marginTop: 12, fontSize: 13 }}>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px dashed var(--tg-border)" }}><span>{t("user.95e1a9")}</span><b>€3,000</b></div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px dashed var(--tg-border)" }}><span>{t("user.145534")}</span><b>€30,000</b></div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px dashed var(--tg-border)" }}><span>Cashback:</span><b style={{ color: "#10F4A7" }}>{t("user.29a90f")}</b></div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0" }}><span>{t("user.9fa7ff")}</span><b style={{ color: frozen ? "#FF3D6C" : "#10F4A7" }}>{frozen ? t("user.frozen") : t("user.active")}</b></div>
          </div>
          <button className="tg-btn ghost" onClick={() => setFrozen(!frozen)} style={{ width: "100%", justifyContent: "center", marginTop: 16 }}
            data-testid="freeze-card-btn">
            {frozen ? t("user.5cfa8d") : t("user.bc3308")}
          </button>
        </Card>
      </div>
      <h3 style={{ margin: "28px 0 14px", fontSize: 16 }}>{t("user.6e2975")}</h3>
      <Card style={{ padding: 0 }}>
        <table className="dash-table">
          <thead><tr><th>{t("pages2.8cdd8b")}</th><th>{t("user.8b1d96")}</th><th>{t("user.8a02e2")}</th><th style={{ textAlign: "right" }}>{t("pages3.cf59eb")}</th></tr></thead>
          <tbody>
            {[
              ["29.04", "Starbucks", t("user.b8160b"), "€4.50"],
              ["28.04", "Uber Eats", "🇦🇪 Dubai", "€28.00"],
              ["27.04", "Apple Store", "🌐 online", "€199.00"],
              ["27.04", t("user.017f50"), t("user.3fe72d"), "€45.20"],
              ["26.04", "Netflix", "🌐 online", "€12.99"],
            ].map((r, i) => <tr key={i}>{r.slice(0, 3).map((c, j) => <td key={j}>{c}</td>)}<td style={{ textAlign: "right", fontWeight: 700 }}>{r[3]}</td></tr>)}
          </tbody>
        </table>
      </Card>
    </Page>
  );
}

/* ═══════════════════ 5. TRANSACTIONS ═══════════════════ */
export function TransactionsPage() {
  const { t } = useT();
  const { transactions, fetchAll } = useStore();
  useAutoRefresh(() => fetchAll(), 15000);
  const [filter, setFilter] = useState("all");
  const filtered = (transactions || []).filter((t) => filter === "all" || t.wallet === filter);
  return (
    <Page title={t("user.cf8ce0")} sub={`${t("user.total-label")}: ${(transactions || []).length}`} testId="transactions-page">
      <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
        {["all", "cash", "shopping", "pension", "insurance"].map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            style={{ padding: "8px 14px", borderRadius: 10, border: "1px solid var(--tg-border)",
              background: filter === f ? "var(--tg-blue)" : "transparent",
              color: filter === f ? "#000" : "var(--tg-text-dim)", fontWeight: 600, cursor: "pointer", fontSize: 13 }}>
            {f}
          </button>
        ))}
        <button className="tg-btn ghost" style={{ marginLeft: "auto", padding: "8px 14px" }} data-testid="export-csv">
          <Download size={14} /> Export CSV
        </button>
      </div>
      <Card style={{ padding: 0 }}>
        <table className="dash-table">
          <thead><tr><th>{t("pages3.3e729b")}</th><th>{t("pages3.345805")}</th><th>{t("user.e9b249")}</th><th>{t("pages.7203f7")}</th><th style={{ textAlign: "right" }}>{t("pages3.cf59eb")}</th></tr></thead>
          <tbody>
            {filtered.map((tx, i) => (
              <tr key={i}>
                <td style={{ color: "var(--tg-text-hint)" }}>{tx.timestamp?.slice(0, 16) || "—"}</td>
                <td>{tx.type}</td>
                <td>{tx.wallet || "—"}</td>
                <td><span style={{ padding: "2px 8px", fontSize: 11, borderRadius: 6, background: "rgba(16,244,167,0.15)", color: "#10F4A7" }}>{tx.status || "ok"}</span></td>
                <td style={{ textAlign: "right", color: (tx.amount || 0) >= 0 ? "#10F4A7" : "#FF3D6C", fontWeight: 700, fontFeatureSettings: '"tnum"' }}>{(tx.amount || 0) >= 0 ? "+" : ""}{fmt(tx.amount)} TGC</td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={5} style={{ textAlign: "center", padding: 40, color: "var(--tg-text-hint)" }}>{t("user.b28935")}</td></tr>}
          </tbody>
        </table>
      </Card>
    </Page>
  );
}

/* ═══════════════════ 6. CHARITY ═══════════════════ */
export function CharityPage() {
  const { t } = useT();
  return (
    <Page title="🐻 Bear Charity Mission" sub={t("user.9dc306")} testId="charity-page">
      <div className="dash-grid dash-grid-3">
        {[
          ["2.4 TGC", t("user.af03d1"), "#FFD700"],
          ["0.8 🐻", t("user.63a07e"), "#FF3D6C"],
          [t("user.f59501"), t("user.802bad"), "#10F4A7"],
        ].map(([v, l, col], i) => (
          <Card key={i} style={{ textAlign: "center" }}>
            <div style={{ fontSize: 32, fontWeight: 900, color: col }}>{v}</div>
            <div style={{ fontSize: 12, color: "var(--tg-text-hint)", marginTop: 6 }}>{l}</div>
          </Card>
        ))}
      </div>
      <h3 style={{ margin: "28px 0 14px", fontSize: 16 }}>{t("user.608b26")}</h3>
      <div className="dash-grid dash-grid-3">
        {[[t("user.d8a32a"), 45], [t("user.4ecb3c"), 30], [t("user.75587d"), 25]].map(([t, pct], i) => (
          <Card key={i}>
            <div style={{ fontWeight: 700 }}>{t}</div>
            <div style={{ height: 8, background: "var(--tg-border)", borderRadius: 4, overflow: "hidden", marginTop: 10 }}>
              <div style={{ height: "100%", width: `${pct}%`, background: "var(--tg-blue)" }} />
            </div>
            <div style={{ fontSize: 12, marginTop: 6, color: "var(--tg-text-hint)" }}>{pct}{t("user.d38314")}</div>
            <button className="tg-btn ghost" style={{ width: "100%", justifyContent: "center", marginTop: 10, padding: "6px" }}>{t("user.23d28c")}</button>
          </Card>
        ))}
      </div>
    </Page>
  );
}

/* ═══════════════════ 7. EXCHANGE ═══════════════════ */
export function ExchangePage() {
  const { t } = useT();
  return (
    <Page title={t("user.971e07")} sub={t("user.7ba0a1")} testId="exchange-page">
      <Card>
        <p style={{ color: "var(--tg-text-dim)", margin: 0 }}>{t("user.713a1f")}<a href="/exchange" style={{ color: "var(--tg-blue)" }}>{t("user.77d191")}</a></p>
        <div className="dash-grid dash-grid-4" style={{ marginTop: 16 }}>
          {[["TGC", "$0.1025", "+2.5%"], ["BTC", "$75,800", "-1.6%"], ["ETH", "$2,249", "-3.3%"], ["SOL", "$142.5", "+4.2%"]].map(([s, p, c], i) => (
            <Card key={i} style={{ padding: 16 }}>
              <div style={{ fontSize: 12, color: "var(--tg-text-hint)" }}>{s}/USDT</div>
              <div style={{ fontSize: 20, fontWeight: 800, marginTop: 4 }}>{p}</div>
              <div style={{ fontSize: 12, color: c.startsWith("+") ? "#10F4A7" : "#FF3D6C", fontWeight: 700 }}>{c}</div>
            </Card>
          ))}
        </div>
      </Card>
    </Page>
  );
}

/* ═══════════════════ 8. P2P ═══════════════════ */
export function P2PPage() {
  const { t } = useT();
  return (
    <Page title="🤝 P2P Exchange" sub={t("user.85c93b")} testId="p2p-page">
      <Card style={{ padding: 0 }}>
        <table className="dash-table">
          <thead><tr><th>{t("functional.3e3eba")}</th><th>{t("functional.682fa8")}</th><th>{t("functional.35536d")}</th><th>{t("functional.e3c1f3")}</th><th></th></tr></thead>
          <tbody>
            {[
              [t("user.9fe815"), "⭐4.9 · 148", "$0.098", "€100-5000", "Kaspi, Card"],
              ["🇦🇪 Marcus T.", "⭐4.8 · 89", "$0.099", "€500-10k", "Revolut, Wise"],
              [t("user.13f580"), "⭐5.0 · 203", "$0.100", "€50-2000", "Humo, Uzcard"],
            ].map((r, i) => (
              <tr key={i}>
                <td><b>{r[0]}</b><br /><span style={{ fontSize: 11, color: "var(--tg-text-hint)" }}>{r[1]}</span></td>
                <td style={{ color: "#10F4A7", fontWeight: 700 }}>{r[2]}</td>
                <td>{r[3]}</td>
                <td>{r[4]}</td>
                <td><button className="tg-btn primary" style={{ padding: "6px 14px", fontSize: 12 }}>{t("functional.d03830")}</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </Page>
  );
}

/* ═══════════════════ 9. SWIFT ═══════════════════ */
export function SwiftPage() {
  const { t } = useT();
  const [sent, setSent] = useState(false);
  return (
    <Page title={t("user.8e1f5a")} sub={t("user.149413")} testId="swift-page">
      <div className="dash-grid dash-grid-2">
        <Card>
          <h3 style={{ margin: "0 0 14px", fontSize: 16 }}>{t("functional.964c55")}</h3>
          {sent ? (
            <div style={{ padding: 20, textAlign: "center", background: "rgba(16,244,167,0.1)", borderRadius: 12 }}>
              <Check size={32} color="#10F4A7" />
              <div style={{ fontWeight: 700, marginTop: 8 }}>{t("user.2bd341")}</div>
              <button className="tg-btn ghost" onClick={() => setSent(false)} style={{ marginTop: 10 }}>{t("functional.964c55")}</button>
            </div>
          ) : (
            <form onSubmit={(e) => { e.preventDefault(); setSent(true); }}>
              <input className="auth-input" placeholder={t("functional.191088")} style={{ marginBottom: 10, paddingLeft: 14 }} />
              <input className="auth-input" placeholder="IBAN" style={{ marginBottom: 10, paddingLeft: 14 }} />
              <input className="auth-input" placeholder="SWIFT/BIC" style={{ marginBottom: 10, paddingLeft: 14 }} />
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10 }}>
                <input className="auth-input" placeholder={t("pages3.cf59eb")} style={{ paddingLeft: 14 }} />
                <select className="auth-input" style={{ paddingLeft: 14 }}><option>EUR</option><option>USD</option><option>GBP</option></select>
              </div>
              <button className="tg-btn primary" style={{ width: "100%", justifyContent: "center", marginTop: 14 }} data-testid="swift-send"><Send size={14} />{t("user.6da0f0")}</button>
            </form>
          )}
        </Card>
        <Card>
          <h3 style={{ margin: "0 0 14px", fontSize: 16 }}>{t("user.41c522")}</h3>
          <table className="dash-table">
            <thead><tr><th>{t("pages2.8cdd8b")}</th><th>{t("user.e3956f")}</th><th>{t("pages3.cf59eb")}</th></tr></thead>
            <tbody>
              <tr><td>29.04</td><td>{t("user.a86c56")}</td><td>€2,500</td></tr>
              <tr><td>25.04</td><td>ACME Ltd</td><td>$8,400</td></tr>
              <tr><td>18.04</td><td>Schmidt GmbH</td><td>€12,000</td></tr>
            </tbody>
          </table>
        </Card>
      </div>
    </Page>
  );
}

/* ═══════════════════ 10. REFERRALS ═══════════════════ */
const REF_PAGE_SIZE = 25;
export function ReferralPage() {
  const { t } = useT();
  const { user } = useStore();
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState(null);
  const [list, setList] = useState({ total: 0, referrals: [] });
  const [refPage, setRefPage] = useState(1);
  const link = `${window.location.origin}/r/${user?.referralCode || "DEMO"}`;
  const copy = () => { navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  useEffect(() => {
    let cancel = false;
    const load = async () => {
      try {
        const [s, l] = await Promise.all([
          api.get("/referral/my"),
          api.get("/referral/list"),
        ]);
        if (!cancel) { setStats(s.data); setList(l.data); }
      } catch (e) { /* fallback to defaults */ }
    };
    load();
    // Auto-refresh every 15 seconds (same cadence as the world map + public leaderboard).
    const id = setInterval(() => { if (!document.hidden) load(); }, 15000);
    return () => { cancel = true; clearInterval(id); };
  }, []);

  const directCount = stats?.directInvites ?? 0;
  const magicProgress = stats?.magicProgress ?? 0;
  const totalNetwork = stats?.totalNetwork ?? 0;
  const totalRewards = stats?.totalRewardsTgc ?? 0;
  const levelCounts = stats?.levelCounts ?? [0, 0, 0, 0, 0, 0, 0, 0];

  return (
    <Page title={t("user.1e9bd1")} sub={t("user.5216cc")} testId="referrals-page">
      <Card style={{ background: "linear-gradient(135deg, rgba(255,215,0,0.1), rgba(0,242,255,0.05))", marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: "var(--tg-blue)", fontWeight: 700 }}>{t("user.5ad26e")}</div>
        <div style={{ display: "flex", gap: 10, marginTop: 10, alignItems: "center" }}>
          <input className="auth-input" value={link} readOnly style={{ paddingLeft: 14 }} data-testid="ref-link-input" />
          <button className="tg-btn primary" onClick={copy} data-testid="copy-ref"><Copy size={14} /> {copied ? t("user.copied") : t("user.copy")}</button>
        </div>
      </Card>

      {/* Stats summary — totals across full network. notranslate prevents Chrome auto-translate from breaking React state. */}
      <div className="dash-grid dash-grid-3 notranslate" translate="no" style={{ marginBottom: 20 }}>
        <Card style={{ textAlign: "center" }} testId="ref-stat-direct">
          <div style={{ fontSize: 12, color: "var(--tg-text-hint)", textTransform: "uppercase", letterSpacing: 1 }}>L1 ({t("ref.direct")})</div>
          <div style={{ fontSize: 32, fontWeight: 900, color: "#FFD700", marginTop: 6 }}>{directCount}</div>
        </Card>
        <Card style={{ textAlign: "center" }} testId="ref-stat-network">
          <div style={{ fontSize: 12, color: "var(--tg-text-hint)", textTransform: "uppercase", letterSpacing: 1 }}>{t("ref.network-total")}</div>
          <div style={{ fontSize: 32, fontWeight: 900, color: "#00F2FF", marginTop: 6 }}>{totalNetwork}</div>
        </Card>
        <Card style={{ textAlign: "center" }} testId="ref-stat-rewards">
          <div style={{ fontSize: 12, color: "var(--tg-text-hint)", textTransform: "uppercase", letterSpacing: 1 }}>{t("ref.rewards-tgc")}</div>
          <div style={{ fontSize: 32, fontWeight: 900, color: "#10F4A7", marginTop: 6 }}>{fmt(totalRewards)}</div>
        </Card>
      </div>

      <h3 style={{ margin: "0 0 14px", fontSize: 16 }}>{t("user.08208f")}</h3>
      <Card style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <span>{t("user.d9a622")}</span><b data-testid="magic-progress">{magicProgress} / 44</b>
        </div>
        <div style={{ height: 10, background: "var(--tg-border)", borderRadius: 5, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${(magicProgress / 44) * 100}%`, background: "linear-gradient(90deg, #FFD700, #00F2FF)", transition: "width 0.4s ease" }} />
        </div>
        <div style={{ fontSize: 12, color: "var(--tg-text-hint)", marginTop: 8 }}>{t("user.e2ceff")}</div>
      </Card>

      {/* MY REFERRALS LIST (L1) */}
      <h3 style={{ margin: "0 0 14px", fontSize: 16 }}>{t("ref.my-list")} ({list.total})</h3>
      <Card style={{ marginBottom: 20, padding: 0, overflow: "hidden" }} testId="ref-list-card">
        {list.referrals.length === 0 ? (
          <div style={{ padding: 32, textAlign: "center", color: "var(--tg-text-hint)" }}>
            <Users size={32} style={{ opacity: 0.5, marginBottom: 10 }} /><br />
            {t("ref.empty")}
          </div>
        ) : (
          <table className="tg-table" style={{ width: "100%", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "rgba(255,255,255,0.02)" }}>
                <th style={{ textAlign: "left", padding: "10px 14px" }}>{t("ref.col.name")}</th>
                <th style={{ textAlign: "left", padding: "10px 14px" }}>Email</th>
                <th style={{ textAlign: "center", padding: "10px 14px" }}>KYC</th>
                <th style={{ textAlign: "right", padding: "10px 14px" }}>{t("ref.col.bonus")}</th>
                <th style={{ textAlign: "right", padding: "10px 14px" }}>{t("ref.col.joined")}</th>
              </tr>
            </thead>
            <tbody>
              {list.referrals.slice((refPage - 1) * REF_PAGE_SIZE, refPage * REF_PAGE_SIZE).map((r) => (
                <tr key={r.userId} style={{ borderTop: "1px solid var(--tg-border)" }} data-testid={`ref-row-${r.userId}`}>
                  <td style={{ padding: "10px 14px", fontWeight: 700 }}>{r.name || "—"}</td>
                  <td style={{ padding: "10px 14px", color: "var(--tg-text-dim)", fontFamily: "monospace", fontSize: 12 }}>{r.email || r.emailMasked || "—"}</td>
                  <td style={{ padding: "10px 14px", textAlign: "center" }}>
                    <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 8,
                      background: r.kycStatus === "approved" ? "rgba(16,244,167,0.15)" : "rgba(255,215,0,0.1)",
                      color: r.kycStatus === "approved" ? "#10F4A7" : "#FFD700" }}>
                      {r.kycStatus === "approved" ? "✓ KYC" : t("ref.kyc-pending")}
                    </span>
                  </td>
                  <td style={{ padding: "10px 14px", textAlign: "right", color: "#10F4A7", fontWeight: 700 }}>+{fmt(r.bonusEarnedTgc)} TGC</td>
                  <td style={{ padding: "10px 14px", textAlign: "right", color: "var(--tg-text-hint)", fontSize: 12 }}>{(r.createdAt || "").slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Referral list pagination */}
      {list.total > REF_PAGE_SIZE && (() => {
        const pages = Math.ceil(list.total / REF_PAGE_SIZE);
        const goRef = (p) => { if (p >= 1 && p <= pages && p !== refPage) setRefPage(p); };
        const items = [];
        items.push(1);
        const win = 1;
        if (refPage - win > 2) items.push("…");
        for (let p = Math.max(2, refPage - win); p <= Math.min(pages - 1, refPage + win); p++) items.push(p);
        if (refPage + win < pages - 1) items.push("…");
        if (pages > 1) items.push(pages);
        return (
          <div data-testid="ref-pagination" style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
            <button className="tg-btn ghost" onClick={() => goRef(refPage - 1)} disabled={refPage <= 1} style={{ padding: "6px 14px", fontSize: 13 }}>‹ Назад</button>
            {items.map((it, i) => (
              it === "…"
                ? <span key={`g${i}`} style={{ color: "var(--tg-text-dim)", padding: "0 4px" }}>…</span>
                : <button key={it} onClick={() => goRef(it)} data-testid={`ref-page-${it}`}
                    style={{
                      minWidth: 36, padding: "6px 10px", borderRadius: 8, fontSize: 13,
                      border: "1px solid " + (it === refPage ? "#FFD700" : "var(--tg-border)"),
                      background: it === refPage ? "rgba(255,215,0,0.15)" : "transparent",
                      color: it === refPage ? "#FFD700" : "var(--tg-text)",
                      fontWeight: it === refPage ? 700 : 400, cursor: "pointer",
                    }}>{it}</button>
            ))}
            <button className="tg-btn ghost" onClick={() => goRef(refPage + 1)} disabled={refPage >= pages} style={{ padding: "6px 14px", fontSize: 13 }}>Вперёд ›</button>
            <span style={{ marginLeft: 12, fontSize: 12, color: "var(--tg-text-hint)" }}>
              Стр. {refPage} из {pages} · всего {list.total}
            </span>
          </div>
        );
      })()}

      <h3 style={{ margin: "0 0 14px", fontSize: 16 }}>{t("user.36b8f9")}</h3>
      <div className="dash-grid dash-grid-4 notranslate" translate="no">
        {[[1, 20], [2, 10], [3, 5], [4, 3], [5, 2], [6, 2], [7, 1], [8, 1]].map(([l, pct]) => {
          const count = levelCounts[l - 1] || 0;
          return (
            <Card key={l} style={{ textAlign: "center", padding: 16 }}>
              <div style={{ fontSize: 12, color: "var(--tg-text-hint)" }}>{t("ref.level")} {l}</div>
              <div style={{ fontSize: 24, fontWeight: 900, color: "var(--tg-blue)", marginTop: 4 }}>{pct}%</div>
              <div style={{ fontSize: 13, color: count > 0 ? "#10F4A7" : "var(--tg-text-dim)", fontWeight: 700, marginTop: 6 }}>
                {count} {t("ref.people")}
              </div>
            </Card>
          );
        })}
      </div>
    </Page>
  );
}

/* ═══════════════════ 11. SECURITY & KYC ═══════════════════ */
export function SecurityPage() {
  const { t } = useT();
  const { user, setToast } = useStore();
  const kyc = user?.kycStatus || "not_started";
  return (
    <Page title="🛡️ Security & KYC" sub={t("user.4c003a")} testId="security-page">
      <div className="dash-grid dash-grid-2">
        <Card>
          <h3 style={{ margin: "0 0 14px", fontSize: 16 }}>{t("user.d6007a")}</h3>
          {[t("user.d801e1"), t("user.354c8b"), t("user.5c9659"), t("user.413734")].map((x, i) => (
            <div key={i} style={{ padding: "12px 0", borderBottom: i < 3 ? "1px dashed var(--tg-border)" : "none", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>{x}</span>
              <button className="tg-btn ghost" onClick={() => setToast("info", `${x} — ${t("user.mobile-only")}`)} style={{ padding: "4px 10px", fontSize: 12 }}>{t("user.0b4604")}</button>
            </div>
          ))}
        </Card>
        <Card>
          <h3 style={{ margin: "0 0 14px", fontSize: 16 }}>{t("user.7a4bf0")}</h3>
          <div data-testid="kyc-locked-notice" style={{
            padding: 24, textAlign: "center",
            background: "linear-gradient(135deg, rgba(255,215,0,0.08), rgba(0,242,255,0.04))",
            border: "1px dashed rgba(255,215,0,0.4)",
            borderRadius: 12,
          }}>
            <div style={{ fontSize: 40 }}>🔒</div>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#FFD700", marginTop: 10 }}>KYC временно закрыт</div>
            <p style={{ fontSize: 13, color: "var(--tg-text-dim)", lineHeight: 1.6, marginTop: 10 }}>
              Верификацию KYC нужно будет проходить после старта проекта и переноса всей базы на собственный блокчейн.
            </p>
          </div>
        </Card>
      </div>
    </Page>
  );
}

/* ═══════════════════ 12. PROFILE ═══════════════════ */
export function ProfilePage() {
  const { t } = useT();
  const { user, updateProfile } = useStore();
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({
    firstName: user?.firstName || "", lastName: user?.lastName || "",
    phone: user?.phone || "", telegram: user?.telegram || "", country: user?.country || "KZ",
  });
  const save = async () => { try { await updateProfile(form); setEdit(false); } catch {} };
  return (
    <Page title={t("user.0f0401")} sub={t("user.51c176")} testId="profile-page">
      <div className="dash-grid dash-grid-2">
        <Card>
          <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 20 }}>
            <div style={{ width: 72, height: 72, borderRadius: 36, background: "linear-gradient(135deg,#00F2FF,#7C3AED)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, fontWeight: 800 }}>
              {user?.firstName?.[0] || "U"}{user?.lastName?.[0] || ""}
            </div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{user?.firstName} {user?.lastName}</div>
              <div style={{ color: "var(--tg-text-hint)", fontSize: 13 }}>{user?.email}</div>
              {user?.createdAt && (
                <div style={{ color: "var(--tg-text-hint)", fontSize: 11, marginTop: 4 }} data-testid="profile-registered-at">
                  📅 {t("user.registered_at") !== "user.registered_at" ? t("user.registered_at") : "Зарегистрирован"}: {new Date(user.createdAt).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                </div>
              )}
            </div>
            <button className="dash-icon-btn" onClick={() => setEdit(!edit)} style={{ marginLeft: "auto" }} data-testid="edit-profile">
              {edit ? <X size={16} /> : <Edit3 size={16} />}
            </button>
          </div>
          {edit ? (
            <div style={{ display: "grid", gap: 10 }}>
              <input className="auth-input" placeholder={t("user.d38d6d")} value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} style={{ paddingLeft: 14 }} data-testid="profile-firstname" />
              <input className="auth-input" placeholder={t("user.a7b7df")} value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} style={{ paddingLeft: 14 }} data-testid="profile-lastname" />
              <input className="auth-input" placeholder={t("functional.2928e1")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} style={{ paddingLeft: 14 }} />
              <input className="auth-input" placeholder="Telegram" value={form.telegram} onChange={(e) => setForm({ ...form, telegram: e.target.value })} style={{ paddingLeft: 14 }} />
              <SearchableCountrySelect value={form.country} onChange={(c) => setForm({ ...form, country: c })} testId="profile-country" placeholder="Страна проживания" />
              <button className="tg-btn gold" onClick={save} style={{ justifyContent: "center" }} data-testid="save-profile"><Save size={14} />{t("user.74ea58")}</button>
            </div>
          ) : (
            <div style={{ fontSize: 14 }} data-testid="profile-readonly">
              {[
                [t("functional.2928e1"), user?.phone || "—"],
                [t("user.8a02e2"), user?.country ? <span data-testid="profile-country-display"><span style={{ fontSize: 18, marginRight: 6 }}>{flagOf(user.country)}</span>{findCountry(user.country).ru} <span style={{ color: "var(--tg-text-hint)", fontSize: 11 }}>({user.country})</span></span> : "—"],
                ["Telegram", user?.telegram || "—"],
                [t("user.505e9b"), user?.dateOfBirth || "—"],
              ].map(([k, v], i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: i < 3 ? "1px dashed var(--tg-border)" : "none" }}><span style={{ color: "var(--tg-text-dim)" }}>{k}:</span><b>{v}</b></div>
              ))}
            </div>
          )}
        </Card>
        <Card>
          <h3 style={{ margin: "0 0 14px", fontSize: 16 }}>{t("user.d36fc1")}</h3>
          {[[t("user.cb71a7"), true], [t("user.6d83f3"), true], [t("user.11c8e3"), false]].map(([l, v], i) => (
            <label key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: i < 2 ? "1px dashed var(--tg-border)" : "none", cursor: "pointer" }}>
              <span>{l}</span>
              <input type="checkbox" defaultChecked={v} style={{ width: 20, height: 20, accentColor: "#00F2FF" }} />
            </label>
          ))}
        </Card>
      </div>
    </Page>
  );
}

/* ═══════════════════ 13. MARKETPLACE ═══════════════════ */
export function MarketplacePage() {
  const { t } = useT();
  const { user, wallets: storeWallets, fetchAll } = useStore();
  const wallets = storeWallets || user?.wallets || {};
  const [products, setProducts] = useState(null);
  const [orders, setOrders] = useState([]);
  const [cart, setCart] = useState({});  // {productId: qty}
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [paying, setPaying] = useState(false);
  const [paySource, setPaySource] = useState("cash");
  const [toast, setToast] = useState(null);

  const reload = async () => {
    try {
      const [p, o] = await Promise.all([
        api.get("/marketplace/products"),
        api.get("/marketplace/orders"),
      ]);
      setProducts(p.data.products || []);
      setOrders(o.data.orders || []);
    } catch (e) { /* noop */ }
  };
  useEffect(() => { reload(); }, []);

  const cats = ["all", ...Array.from(new Set((products || []).map((p) => p.category)))];
  const filtered = (products || []).filter((p) =>
    (category === "all" || p.category === category) &&
    (!search || p.name.toLowerCase().includes(search.toLowerCase()))
  );

  const cartItems = Object.entries(cart).map(([pid, qty]) => {
    const p = products?.find((x) => x.productId === pid);
    return p ? { ...p, qty } : null;
  }).filter(Boolean);
  const cartTotalEur = cartItems.reduce((s, it) => s + it.priceEur * it.qty, 0);
  const cartTotalTGC = cartItems.reduce((s, it) => s + it.priceTGC * it.qty, 0);
  const cartCount = cartItems.reduce((s, it) => s + it.qty, 0);

  const addToCart = (pid) => setCart((c) => ({ ...c, [pid]: (c[pid] || 0) + 1 }));
  const incQty = (pid) => setCart((c) => ({ ...c, [pid]: (c[pid] || 0) + 1 }));
  const decQty = (pid) => setCart((c) => {
    const n = (c[pid] || 0) - 1;
    if (n <= 0) { const cp = { ...c }; delete cp[pid]; return cp; }
    return { ...c, [pid]: n };
  });
  const removeItem = (pid) => setCart((c) => { const cp = { ...c }; delete cp[pid]; return cp; });
  const clearCart = () => setCart({});

  const sourceWallet = paySource === "cash" ? "cashTgc" : "shoppingTgc";
  const sourceBalance = wallets?.[sourceWallet] || 0;
  const canCheckout = cartCount > 0 && sourceBalance >= cartTotalTGC && !paying;

  const checkout = async () => {
    if (!canCheckout) return;
    setPaying(true);
    try {
      const items = Object.entries(cart).map(([productId, qty]) => ({ productId, qty }));
      const r = await api.post("/marketplace/checkout", { items, wallet: paySource });
      setToast({ type: "ok", text: `${t("user.order")} ${r.data.orderId.slice(0, 12)} ${t("user.paid")} · €${r.data.totalEur.toFixed(2)}` });
      clearCart();
      await Promise.all([reload(), fetchAll()]);
    } catch (e) {
      setToast({ type: "err", text: e.response?.data?.detail || t("user.bb4109") });
    } finally {
      setPaying(false);
      setTimeout(() => setToast(null), 4000);
    }
  };

  return (
    <Page title="🛍️ Marketplace" sub={t("user.721a52")} testId="marketplace-page">
      {/* Toast */}
      {toast && (
        <div data-testid="mk-toast" style={{
          position: "fixed", top: 80, right: 24, zIndex: 1000, padding: "14px 20px",
          background: toast.type === "ok" ? "rgba(16,244,167,0.15)" : "rgba(255,61,108,0.15)",
          border: `1px solid ${toast.type === "ok" ? "#10F4A7" : "#FF3D6C"}`,
          color: toast.type === "ok" ? "#10F4A7" : "#FF8FA6",
          borderRadius: 12, fontSize: 13, fontWeight: 700, maxWidth: 380,
        }}>{toast.text}</div>
      )}

      {/* Filter bar */}
      <Card style={{ marginBottom: 18 }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <input className="auth-input" placeholder={t("user.3418b6")} value={search} onChange={(e) => setSearch(e.target.value)} style={{ flex: 1, minWidth: 200 }} data-testid="mk-search" />
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {cats.map((c) => (
              <button key={c} onClick={() => setCategory(c)} data-testid={`mk-cat-${c}`}
                style={{
                  padding: "8px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: "pointer",
                  border: "1px solid " + (category === c ? "var(--tg-blue)" : "var(--tg-border)"),
                  background: category === c ? "rgba(0,242,255,0.12)" : "transparent",
                  color: category === c ? "var(--tg-blue)" : "var(--tg-text-dim)",
                }}>{c === "all" ? t("user.all") : c}</button>
            ))}
          </div>
        </div>
      </Card>

      <div style={{ display: "grid", gridTemplateColumns: cartCount > 0 ? "1fr 360px" : "1fr", gap: 18 }}>
        {/* Products grid */}
        <div>
          <div className="dash-grid dash-grid-3">
            {!products && <div style={{ color: "var(--tg-text-hint)" }}>{t("user.89d69a")}</div>}
            {filtered.map((p) => (
              <Card key={p.productId} style={{ padding: 0, overflow: "hidden" }} data-testid={`mk-prod-${p.productId}`}>
                <div style={{
                  height: 160, background: `url(${p.image}) center/cover`, position: "relative",
                }}>
                  <span style={{ position: "absolute", top: 10, left: 10, padding: "3px 8px", background: "rgba(0,0,0,0.65)", color: "var(--tg-blue)", fontSize: 10, borderRadius: 6, fontWeight: 700, textTransform: "uppercase" }}>{p.category}</span>
                </div>
                <div style={{ padding: 14 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, minHeight: 38 }}>{p.name}</div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
                    <div>
                      <div style={{ fontSize: 18, fontWeight: 900, color: "#FFD700" }}>€{p.priceEur}</div>
                      <div style={{ fontSize: 10, color: "var(--tg-text-hint)" }}>{p.priceTGC.toLocaleString("ru-RU")} TGC</div>
                    </div>
                    <button className="tg-btn primary" onClick={() => addToCart(p.productId)} data-testid={`mk-add-${p.productId}`}
                      style={{ padding: "8px 14px", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <Plus size={12} />{t("user.5aa28e")}</button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Cart sidebar (sticky) */}
        {cartCount > 0 && (
          <div>
            <Card style={{ position: "sticky", top: 100 }} data-testid="mk-cart">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <h3 style={{ margin: 0, fontSize: 15 }}>{t("user.c8f4ed")} {cartCount}</h3>
                <button onClick={clearCart} className="dash-icon-btn" data-testid="mk-cart-clear" title={t("user.707436")}><X size={14} /></button>
              </div>

              {cartItems.map((it) => (
                <div key={it.productId} style={{ display: "flex", gap: 10, marginBottom: 12, paddingBottom: 12, borderBottom: "1px dashed var(--tg-border)" }}>
                  <div style={{ width: 50, height: 50, borderRadius: 8, background: `url(${it.image}) center/cover`, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.name}</div>
                    <div style={{ fontSize: 11, color: "#FFD700", fontWeight: 700, marginTop: 2 }}>€{(it.priceEur * it.qty).toFixed(2)}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                      <button onClick={() => decQty(it.productId)} className="dash-icon-btn" style={{ width: 24, height: 24 }} data-testid={`mk-dec-${it.productId}`}>−</button>
                      <span style={{ fontSize: 12, fontWeight: 700, minWidth: 20, textAlign: "center" }}>{it.qty}</span>
                      <button onClick={() => incQty(it.productId)} className="dash-icon-btn" style={{ width: 24, height: 24 }} data-testid={`mk-inc-${it.productId}`}>+</button>
                      <button onClick={() => removeItem(it.productId)} className="dash-icon-btn" style={{ width: 24, height: 24, marginLeft: "auto", color: "#FF3D6C" }}>×</button>
                    </div>
                  </div>
                </div>
              ))}

              {/* Pay-from selector */}
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 10, color: "var(--tg-text-dim)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>{t("user.96f3fd")}</div>
                <div style={{ display: "flex", gap: 6 }}>
                  {[
                    ["cash",     "Cash",     wallets?.cashTgc || 0],
                    ["shopping", "Shopping", wallets?.shoppingTgc || 0],
                  ].map(([key, label, bal]) => (
                    <button key={key} onClick={() => setPaySource(key)} data-testid={`mk-pay-${key}`}
                      style={{
                        flex: 1, padding: "8px 6px", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer",
                        border: "1px solid " + (paySource === key ? "var(--tg-blue)" : "var(--tg-border)"),
                        background: paySource === key ? "rgba(0,242,255,0.12)" : "transparent",
                        color: paySource === key ? "var(--tg-blue)" : "var(--tg-text-dim)",
                      }}>
                      {label}<br /><span style={{ fontSize: 10, opacity: 0.7 }}>{fmt(bal)} TGC</span>
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--tg-border)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontSize: 12, color: "var(--tg-text-dim)" }}>{t("user.edcf39")}</span>
                  <span style={{ fontSize: 18, fontWeight: 900, color: "#FFD700" }}>€{cartTotalEur.toFixed(2)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
                  <span style={{ fontSize: 11, color: "var(--tg-text-hint)" }}>{t("user.600961")}</span>
                  <span style={{ fontSize: 12, color: "var(--tg-blue)", fontWeight: 700 }}>{cartTotalTGC.toLocaleString("ru-RU")} TGC</span>
                </div>
                {!canCheckout && cartCount > 0 && sourceBalance < cartTotalTGC && (
                  <div style={{ padding: 10, background: "rgba(255,61,108,0.1)", borderRadius: 8, fontSize: 11, color: "#FF8FA6", marginBottom: 12 }}>
                    <AlertCircle size={12} style={{ verticalAlign: "middle", marginRight: 6 }} />
                    {t("user.insufficient-tgc")} {paySource}. {t("user.missing")} {fmt(cartTotalTGC - sourceBalance)} TGC.
                  </div>
                )}
                <button className="tg-btn gold" onClick={checkout} disabled={!canCheckout} data-testid="mk-checkout"
                  style={{ width: "100%", padding: 12, fontWeight: 800, opacity: canCheckout ? 1 : 0.5 }}>
                  {paying ? t("user.2c0b32") : t("user.a64b93")}
                </button>
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* Orders history */}
      <h3 style={{ margin: "28px 0 14px", fontSize: 16 }}>{t("user.26df5f")}{orders.length})</h3>
      {orders.length === 0 ? (
        <Card><p style={{ color: "var(--tg-text-hint)", margin: 0, textAlign: "center", padding: 20 }}>{t("user.c9d06c")}</p></Card>
      ) : (
        <div className="dash-grid dash-grid-2">
          {orders.map((o) => (
            <Card key={o.orderId} data-testid={`mk-order-${o.orderId}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <div>
                  <div style={{ fontFamily: "monospace", fontSize: 11, color: "var(--tg-blue)" }}>{o.orderId}</div>
                  <div style={{ fontSize: 10, color: "var(--tg-text-hint)", marginTop: 2 }}>{new Date(o.createdAt).toLocaleString("ru-RU")} · {o.wallet}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 16, fontWeight: 900, color: "#FFD700" }}>€{o.totalEur.toFixed(2)}</div>
                  <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 6, background: "rgba(16,244,167,0.1)", color: "#10F4A7" }}>● {o.status}</span>
                </div>
              </div>
              <div style={{ fontSize: 11, color: "var(--tg-text-dim)" }}>
                {o.items.map((it) => `${it.name} ×${it.qty}`).join(" · ")}
              </div>
            </Card>
          ))}
        </div>
      )}
    </Page>
  );
}

/* ═══════════════════ 14. ACADEMY ═══════════════════ */
export function AcademyPage() {
  const { t } = useT();
  return (
    <Page title="🎓 Turbo Academy" sub={t("user.688cd1")} testId="academy-page">
      <Card style={{ textAlign: "center", padding: 48, background: "linear-gradient(135deg, rgba(255,61,108,0.08), rgba(124,58,237,0.06))" }}>
        <GraduationCap size={64} color="#FF3D6C" />
        <h2 style={{ marginTop: 14 }}>{t("user.30e6e1")}</h2>
        <p style={{ color: "var(--tg-text-dim)", maxWidth: 500, margin: "12px auto 0" }}>{t("user.bea5c7")}</p>
      </Card>
      <h3 style={{ margin: "28px 0 14px", fontSize: 16 }}>{t("user.720ba1")}</h3>
      <div className="dash-grid dash-grid-3">
        {[["Crypto 101", "🚀", "50 TGC"], ["Trading Basics", "📈", "150 TGC"], ["Staking Deep Dive", "💎", "200 TGC"], ["Security", "🛡️", "100 TGC"], ["Tokenomics", "🪙", "300 TGC"], ["Masterclass", "👑", "500 TGC"]].map(([t, ic, r], i) => (
          <Card key={i}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>{ic}</div>
            <div style={{ fontWeight: 700 }}>{t}</div>
            <div style={{ fontSize: 12, color: "#FFD700", marginTop: 6 }}>{t("user.f2170c")} {r}</div>
          </Card>
        ))}
      </div>
    </Page>
  );
}
